import React, { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, ExternalLink, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { useEthereum } from '@/context/EthereumContext';
import { formatDistanceToNow } from 'date-fns';

const EthereumTransactionHistory: React.FC = () => {
  const { transactions, loadTransactions } = useEthereum();
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadTransactions(50, 0);
  }, []);

  const handleLoadMore = async () => {
    setLoading(true);
    try {
      await loadTransactions(50, transactions.length);
    } catch (error) {
      console.error('Failed to load more transactions:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500 animate-pulse" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getTypeIcon = (type: string) => {
    return type === 'deposit' ? (
      <ArrowDown className="h-4 w-4 text-green-500" />
    ) : (
      <ArrowUp className="h-4 w-4 text-red-500" />
    );
  };

  if (transactions.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">No transactions yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Transaction History</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {transactions.map((tx) => (
            <div
              key={tx._id || tx.txHash}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-muted rounded-full">
                  {getTypeIcon(tx.type)}
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">
                      {tx.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                    </span>
                    {getStatusIcon(tx.status)}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(tx.createdAt), { addSuffix: true })}
                  </p>
                  {tx.txHash && (
                    <p className="text-xs text-muted-foreground font-mono">
                      {tx.txHash.slice(0, 10)}...{tx.txHash.slice(-8)}
                    </p>
                  )}
                </div>
              </div>

              <div className="text-right">
                <p className="font-medium">
                  {tx.amount} ETH
                </p>
                <p className="text-sm text-muted-foreground">
                  ${tx.amountUsd?.toFixed(2)}
                </p>
                {tx.explorerUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    asChild
                    className="mt-1"
                  >
                    <a
                      href={tx.explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {hasMore && (
          <Button
            variant="outline"
            className="w-full"
            onClick={handleLoadMore}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default EthereumTransactionHistory;
